import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthLayout from "../components/AuthLayout";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { safeAuthDestination } from "../lib/authDestination";
import PasswordInput from "../components/PasswordInput";
import { LEGAL_VERSION } from "../config/legal";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get("plan")?.toLowerCase();
  const requestedNext = searchParams.get("next") || "";
  const next = safeAuthDestination(requestedNext);
  const selectedPlan = ["starter", "growth", "pro"].includes(requestedPlan) ? requestedPlan : "";
  const planLabel = selectedPlan ? selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1) : "";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const destination = next || (selectedPlan ? `/checkout/${selectedPlan}` : "/onboarding");
  useEffect(() => { if (user) navigate(destination, { replace: true }); }, [user, navigate, destination]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 12 || !/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      toast.error("Use at least 12 characters with uppercase, lowercase, and a number."); return;
    }
    if (!accepted) { toast.error("Review and accept the Terms and Privacy Notice to continue."); return; }
    setLoading(true);
    try {
      await register({ ...form, terms_accepted: true, legal_version: LEGAL_VERSION });
      navigate(next || (selectedPlan ? `/checkout/${selectedPlan}` : "/onboarding"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

  return (
    <AuthLayout>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Create account</h1>
      <p className="mt-1 text-sm text-slate-500">Start optimizing your CRM data quality.</p>
      {selectedPlan && <div data-testid="selected-plan-summary" className="mt-5 border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Selected plan</div>
        <div className="mt-1 text-sm font-semibold text-slate-900">{planLabel}</div>
        <div className="mt-1 text-xs text-slate-500">You will continue to secure checkout after creating your account.</div>
      </div>}

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Full name</label>
          <input data-testid="register-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Email</label>
          <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Password</label>
          <PasswordInput data-testid="register-password-input" toggleTestId="register-password-toggle"
                         autoComplete="new-password" minLength={12} maxLength={72} required value={form.password}
                         onChange={set("password")} className={inputCls} placeholder="12+ characters, mixed case and number" />
        </div>
        <label className="flex items-start gap-3 border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
          <input data-testid="register-legal-consent" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" />
          <span>I agree to the <Link to="/terms" target="_blank" className="font-semibold text-slate-900 underline">Terms of Service</Link> and acknowledge the <Link to="/privacy" target="_blank" className="font-semibold text-slate-900 underline">Privacy Notice</Link>.</span>
        </label>
        <button data-testid="register-submit-button" type="submit" disabled={loading || !accepted}
                className="w-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-sm text-slate-500">
        Already have an account?{" "}
        <Link to={next ? `/login?next=${encodeURIComponent(next)}` : selectedPlan ? `/login?plan=${selectedPlan}` : "/login"} data-testid="go-to-login-link" className="font-medium text-slate-900 underline underline-offset-2">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
